import { useCallback, useMemo } from 'react';
import { http } from '@/lib/http';
import { toast } from 'sonner';
import { useIsMobile } from '@/hooks/use-is-mobile';
import { useAppStatusStore } from '@/stores/appStatus';
import { useSettingsStore } from '@/stores/settings';
import { useWorkspaceStore } from '@/stores/workspace';
import { useProjectsStore } from '@/stores/projects';
import { useInstructionsStore } from '@/stores/instructions';
import { measure } from '@/lib/performance';
import { useInteractiveGenerationStore } from '@/stores/interactiveGenerationStore';
type ClipboardCopyStrategy = 'api' | 'textarea' | 'prompt';

const copyWithTextarea = (text: string): ClipboardCopyStrategy | null => {
  if (typeof document === 'undefined') return null;

  let textarea: HTMLTextAreaElement | null = null;
  try {
    textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.top = '0';
    textarea.style.left = '0';
    textarea.style.opacity = '0';
    textarea.style.pointerEvents = 'none';

    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    if (typeof textarea.setSelectionRange === 'function') {
      textarea.setSelectionRange(0, textarea.value.length);
    }

    const supported = typeof document.execCommand === 'function';
    const successful = supported ? document.execCommand('copy') : false;

    return successful ? 'textarea' : null;
  } catch (error) {
    console.warn('Fallback clipboard copy failed', error);
    return null;
  } finally {
    if (textarea && textarea.parentNode) {
      textarea.parentNode.removeChild(textarea);
    }
  }
};

const copyWithPrompt = (text: string): ClipboardCopyStrategy | null => {
  if (typeof window === 'undefined') return null;
  toast.info('Copy the generated context from the prompt and tap OK when finished.');
  const response = window.prompt(
    'Clipboard access is not available. Copy the generated context below and tap OK when you are done.',
    text
  );
  return response !== null ? 'prompt' : null;
};

export type CopyContextResult = {
  context?: string;
  copyStrategy?: ClipboardCopyStrategy;
  servedToClipboard?: boolean;
  mode: 'automatic' | 'manual';
};

export const copyTextToClipboard = async (text: string): Promise<ClipboardCopyStrategy> => {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return 'api';
    } catch (error) {
      console.warn('navigator.clipboard.writeText failed, falling back', error);
    }
  }

  const textareaResult = copyWithTextarea(text);
  if (textareaResult) return textareaResult;

  const promptResult = copyWithPrompt(text);
  if (promptResult) return promptResult;

  throw new Error('Clipboard copy is not supported in this environment.');
};

export const useContextBuilder = () => {
  const {
    userSelectedFiles,
    contextSelectedFiles,
    includeFileTree,
    tokenBudget,
    treeStringTokenCount,
    instructions,
    fileTree,
  } = useWorkspaceStore(state => ({
    userSelectedFiles: state.userSelectedFiles,
    contextSelectedFiles: state.contextSelectedFiles,
    includeFileTree: state.includeFileTree,
    tokenBudget: state.tokenBudget,
    treeStringTokenCount: state.treeStringTokenCount,
    fileTree: state.fileTree,
    instructions: state.instructions,
  }));
  const { activeProjectId, byId: projectsById } = useProjectsStore();
  const activeProject = activeProjectId ? projectsById[activeProjectId] : null;
  const { interactionMode, llmProviderId } = useSettingsStore((state) => state.workspace) || { interactionMode: 'copy' };
  const isMobile = useIsMobile();
  const { llmProviders } = useSettingsStore();
  const { toolAvailability } = useAppStatusStore();

  const allFilePaths = useMemo(() => {
    const paths = new Set<string>();
    const traverse = (nodes: any[]) => {
      nodes.forEach((node) => {
        if (node.type === 'file') {
          paths.add(node.path);
        }
        if (node.children) traverse(node.children);
      });
    };
    traverse(fileTree);
    return new Set(paths);
  }, [fileTree]);

  const combinedSelectedFiles = useMemo(() => {
    const all = new Set(userSelectedFiles);
    contextSelectedFiles.forEach((files) => {
      files.forEach((file) => all.add(file));
    });
    return all;
  }, [userSelectedFiles, contextSelectedFiles]);

  const filesFromInstructions = useMemo(() => {
    const foundFiles = new Set<string>();
    if (!instructions) {
      return foundFiles;
    }
    // A simple heuristic: look for words that are valid file paths.
    // This avoids matching parts of other words.
    const potentialPaths = instructions.match(/[\w/.-]+/g) || [];

    for (const potentialPath of potentialPaths) {
        if (allFilePaths.has(potentialPath)) {
            foundFiles.add(potentialPath);
        }
    }
    return foundFiles;
  }, [instructions, allFilePaths]);

  const finalFiles = useMemo(() => new Set([...Array.from(combinedSelectedFiles), ...Array.from(filesFromInstructions)]), [
    combinedSelectedFiles,
    filesFromInstructions,
  ]);

  const totalTokens = useMemo(() => {
    let count = 0;
    const fileNodeMap = new Map<string, any>();
    const traverse = (nodes: any[]) => {
      nodes.forEach((node) => {
        fileNodeMap.set(node.path, node);
        if (node.children) traverse(node.children);
      });
    };
    traverse(fileTree);

    finalFiles.forEach((path) => {
      const node = fileNodeMap.get(path);
      if (node && node.tokens) count += node.tokens;
    });

    if (includeFileTree) count += treeStringTokenCount;
    return count;
  }, [finalFiles, fileTree, includeFileTree, treeStringTokenCount]);

  const handleCopyContext = useCallback(measure(async (): Promise<CopyContextResult | undefined> => {
    const instructions = useInstructionsStore.getState().instructions;
    if (finalFiles.size === 0) {
      if (!instructions.trim()) {
        toast.warning('Please provide instructions to build context automatically.');
        return;
      }

      const budgetValue = tokenBudget === 'unlimited' ? 1000000 : tokenBudget;
      const toastId = toast.loading('Building automatic context...');

        try {
            if (isMobile) {
                const { context } = await http<{ context: string }>('/api/context/build_from_instructions', {
                    method: 'POST',
                    body: JSON.stringify({ instructions, tokenBudget: budgetValue, serveToClipboard: false }),
                });
                toast.success('Automatic context ready!', { id: toastId });
                return { context, mode: 'automatic' };
            } else {
                // Desktop: Ask server to build and copy directly to clipboard
                await http<void>('/api/context/build_from_instructions', {
                    method: 'POST',
                    body: JSON.stringify({ instructions, tokenBudget: budgetValue, serveToClipboard: true }),
                });
                toast.success('Context successfully copied!', { id: toastId });
                return { servedToClipboard: true, mode: 'automatic' };
            }
        } catch (error) {
            toast.dismiss(toastId);
            throw error;
        }
    } else {
      const requestBody = {
        basePath: activeProject?.serverPath,
        files: Array.from(finalFiles),
        instructions: instructions,
        includeFileTree: includeFileTree,
      };

      if (isMobile) {
        const data = await http<{ context: string }>('/api/context/build_from_files', {
          method: 'POST',
          body: JSON.stringify(requestBody),
        });
        const copyStrategy = await copyTextToClipboard(data.context);
        toast.success('Context successfully copied!');
        return { context: data.context, mode: 'manual', copyStrategy };
      } else {
        await http<void>('/api/context/serve_to_clipboard', {
          method: 'POST',
          body: JSON.stringify(requestBody),
        });
        toast.success('Context successfully copied!');
        return { servedToClipboard: true, mode: 'manual' };
      }
    }
  }, 'handleCopyContext'), [activeProject?.serverPath, finalFiles, includeFileTree, isMobile, tokenBudget]);

  const canGenerate = useMemo(() => {
    const currentMode = interactionMode;
    if (currentMode === 'copy') {
      // YOLO mode is triggered by 'copy'. It needs Qwen for evaluation and at least one other provider.
      const isQwenAvailable = toolAvailability?.qwen ?? false;
      const availableCliGenerators = Object.entries(toolAvailability || {})
        .filter(([tool, available]) => available && tool !== 'qwen').length;
      const availableCustomGenerators = Object.values(llmProviders || {}).filter(p => p.enabled).length;
      return isQwenAvailable && (availableCliGenerators + availableCustomGenerators > 0);
    }
    if (currentMode === 'llm_provider') {
      return !!llmProviderId && llmProviders?.[llmProviderId]?.enabled;
    }
    // For specific built-in providers
    return toolAvailability?.[currentMode] ?? false;

  }, [interactionMode, llmProviderId, toolAvailability, llmProviders]);

  const handleGenerateProposal = useCallback(measure(async () => {
    const instructions = useInstructionsStore.getState().instructions;
      const isInteractiveGeneration = useInteractiveGenerationStore.getState().isInteractiveGeneration;
    
    if (!canGenerate) {
      toast.warning('The selected AI provider is not available.', { description: 'Please check your settings or install the required tools.' });
    } else if (finalFiles.size === 0 && !instructions.trim()) {
      toast.warning('Cannot generate proposal without files or instructions.');
      return;
    }

    if (interactionMode === 'copy') { // This is YOLO mode
      const toastId = toast.loading('Starting YOLO mode...');
      try {
        const payload: any = { instructions };

        if (finalFiles.size === 0) {
          payload.files = [];
          payload.tokenBudget = tokenBudget !== 'unlimited' ? tokenBudget : 1000000;
        } else {
          payload.files = Array.from(finalFiles);
          payload.includeFileTree = includeFileTree;
        }

        const endpoint = '/api/proposals/run-yolo';
        const response = await http<{ taskId: string }>(endpoint, {
          method: 'POST',
          body: JSON.stringify(payload),
        });

        toast.dismiss(toastId);
        toast.success('YOLO mode started. All providers are generating proposals.');
        return { taskId: response.taskId, isYolo: true };
      } catch (error: any) {
        toast.dismiss(toastId);
        toast.error('Failed to start YOLO mode', {
          description: error.message || 'An unknown error occurred.',
        });
      }
      return;
    }

    const toastId = toast.loading('Starting proposal generation...');

    try {
      const payload: any = { instructions, interactive: isInteractiveGeneration };

      if (finalFiles.size === 0) {
        payload.files = [];
        payload.tokenBudget = tokenBudget !== 'unlimited' ? tokenBudget : 1000000;
      } else {
        payload.files = Array.from(finalFiles);
        payload.includeFileTree = includeFileTree;
      }

      let endpoint: string;
      if (interactionMode === 'llm_provider' && llmProviderId) {
        endpoint = '/api/proposals/run-llm-provider';
        payload.providerId = llmProviderId;
      } else {
        switch (interactionMode) {
          case 'gemini':
            endpoint = '/api/proposals/run-gemini';
            break;
          case 'codex':
            endpoint = '/api/proposals/run-codex';
            break;
          case 'copilot':
            endpoint = '/api/proposals/run-copilot';
            break;
          case 'claude':
            endpoint = '/api/proposals/run-claude';
            break;
          default: // qwen is the other option for now
            endpoint = '/api/proposals/run-qwen';
            break;
        }
      }
      const response = await http<{ taskId: string, proposalId: string, interactive: boolean }>(endpoint, {
        method: 'POST',
        body: JSON.stringify(payload),
      });

      console.log('Proposal generation started:', response);

      toast.dismiss(toastId);
      const modelForNav = interactionMode === 'llm_provider' ? `llm_provider:${llmProviderId}` : interactionMode;
      return { taskId: response.taskId, model: modelForNav, proposalId: response.proposalId, interactive: response.interactive };
    } catch (error: any) {
      toast.dismiss(toastId);
      toast.error('Failed to start generation', {
        description: error.message || 'An unknown error occurred.',
      });
    }
  }, 'handleGenerateProposal'), [finalFiles, includeFileTree, tokenBudget, interactionMode, llmProviderId, canGenerate]);

  const isAutomaticMode = finalFiles.size === 0;

  // Return 'yolo' as interactionMode to the UI when in 'copy' mode to ensure the generate button is visible.
  const uiInteractionMode = (interactionMode === 'copy' && canGenerate) ? 'yolo' : interactionMode;

  return {
    handleCopyContext,
    handleGenerateProposal,
    totalTokens,
    isAutomaticMode,
    interactionMode: uiInteractionMode, // Keep this for display logic
    canGenerate, // Use this for enabling/disabling actions
  };
};